import { useEffect, useRef, useState } from 'react';
import { ListTree } from 'lucide-react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { toast } from '@/lib/toast';
import { useAiSettings, useCreateBraindump } from '@/features/inbox/useBraindump';

const DRAFT_KEY = 'chronos-capture-draft';

function initialText(): string {
  const params = new URLSearchParams(window.location.search);
  const shared = params.get('text');
  if (shared) return shared;
  try {
    return localStorage.getItem(DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

interface Props {
  /** Compact layout when embedded in a sheet (no page hero padding). */
  embedded?: boolean;
  onDone?: () => void;
}

/**
 * Capture surface: FAB, share target, or sheet.
 * One field, one button; nothing lands until Inbox review.
 */
export function CapturePage({ embedded = false, onDone }: Props) {
  const [text, setText] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const ai = useAiSettings();
  const create = useCreateBraindump();

  const configured = ai.data?.configured && ai.data.enabled;

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    if (text) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  }, [text]);

  const submit = () => {
    if (!text.trim()) return;
    create.mutate(text.trim(), {
      onSuccess: (dump) => {
        setText('');
        localStorage.removeItem(DRAFT_KEY);
        // Drop share-target params so a refresh does not re-open capture.
        const params = new URLSearchParams(window.location.search);
        if (params.get('view') === 'capture' || params.has('text')) {
          params.delete('view');
          params.delete('text');
          const qs = params.toString();
          window.history.replaceState(null, '', qs ? `/?${qs}` : '/');
        }
        if (dump.status === 'failed') return;
        toast.show(
          `Found ${dump.items.length} ${dump.items.length === 1 ? 'task' : 'tasks'}: review in Inbox`,
        );
        onDone?.();
      },
    });
  };

  return (
    <div
      className={cn(
        embedded
          ? 'flex flex-col'
          : 'mx-auto flex min-h-[70dvh] w-full max-w-lg flex-col justify-center px-5 py-8',
      )}
    >
      {!embedded && (
        <>
          <h1 className="text-title font-semibold">Capture</h1>
          <p className="mt-1 mb-4 text-small text-text-secondary">
            Dump it here. Nothing is added until you review it in Inbox.
          </p>
        </>
      )}

      {embedded && (
        <p className="mb-4 text-small text-text-secondary">
          Dump it here. Nothing is added until you review it in Inbox.
        </p>
      )}

      {ai.data && !configured && (
        <p className="mb-3 text-small text-warning">
          Add an Anthropic key in Settings → AI parsing to sort captures automatically.
        </p>
      )}

      <label htmlFor="capture-text" className="sr-only">
        Capture
      </label>
      <textarea
        ref={textareaRef}
        id="capture-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        rows={embedded ? 8 : 6}
        disabled={!configured || create.isPending}
        placeholder="Use the keyboard mic to dictate…"
        className={cn(
          'w-full resize-y rounded-lg border border-border bg-surface-1 p-3',
          'text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border disabled:opacity-50',
        )}
      />

      <div className="mt-3 flex justify-end">
        <Button
          variant="primary"
          onClick={submit}
          disabled={!configured || !text.trim()}
          loading={create.isPending}
        >
          <ListTree size={15} strokeWidth={1.5} aria-hidden />
          {create.isPending ? 'Reading…' : 'Sort it out'}
        </Button>
      </div>
    </div>
  );
}
